<script lang="ts">
    import type { Snippet } from 'svelte';
    import type { ImageAspectRatio } from '@tile/components/product-image/types';
    import type { TileLayout } from '@tilelist/domain/composition/BracketLayout';

    interface Props {
        isLoading: boolean;
        href?: string;
        title?: string;
        aspectRatio?: ImageAspectRatio;
        tileLayout?: TileLayout;
        children?: Snippet;
    }

    let { isLoading, href, title, aspectRatio, tileLayout, children }: Props = $props();

    const imagePaddingTop = $derived.by(() => {
        switch (aspectRatio) {
            case 'landscape':
                return '69.5%';
            case 'portrait':
                return '143%';
            default:
                return '100%';
        }
    });
</script>

{#if isLoading}
    <div
        class="reptile-skeleton-tile"
        class:reptile-skeleton-tile--portrait={aspectRatio === 'portrait' || !aspectRatio}
        class:reptile-skeleton-tile--landscape={aspectRatio === 'landscape'}
        class:reptile-skeleton-tile--quad={aspectRatio === 'quad'}
        class:reptile-skeleton-tile--fashion={tileLayout === 'fashion'}
    >
        <!-- SEO link (keeps product URL accessible to crawlers) -->
        {#if href}
            <oc-link-v2>
                <a class="oc-visually-hidden" {href} {title}>{title}</a>
            </oc-link-v2>
        {/if}

        <!-- Image skeleton -->
        <div class="reptile-skeleton-tile__image-wrapper" style:padding-top={imagePaddingTop}>
            <oc-skeleton-v1
                class="reptile-skeleton-tile__image"
                width="100%"
                height="100%"
                border-radius="var(--oc-semantic-border-radius-50) var(--oc-semantic-border-radius-50) 0 0"
            ></oc-skeleton-v1>
        </div>

        <!-- Content area -->
        <div class="reptile-skeleton-tile__content">
            <!-- Title block -->
            <div class="reptile-skeleton-tile__title">
                <!-- Brand line: hidden in fashion layout -->
                {#if tileLayout !== 'fashion'}
                    <oc-skeleton-v1
                        variant="rectangle"
                        border-radius=""
                        width="50%"
                        height="var(--oc-semantic-spacing-relative-75)"
                    ></oc-skeleton-v1>
                {/if}
                <oc-skeleton-v1
                    variant="rectangle"
                    border-radius=""
                    width="100%"
                    height="var(--oc-semantic-spacing-relative-75)"
                ></oc-skeleton-v1>
                <oc-skeleton-v1
                    variant="rectangle"
                    border-radius=""
                    width="80%"
                    height="var(--oc-semantic-spacing-relative-75)"
                ></oc-skeleton-v1>
            </div>

            <!-- Price -->
            <oc-skeleton-v1
                class="reptile-skeleton-tile__price"
                variant="rectangle"
                border-radius=""
                width="30%"
                height="var(--oc-semantic-spacing-relative-75)"
            ></oc-skeleton-v1>

            <!-- Delivery info: hidden in fashion layout -->
            {#if tileLayout !== 'fashion'}
                <div class="reptile-skeleton-tile__delivery">
                    <oc-skeleton-v1
                        variant="rectangle"
                        border-radius=""
                        width="100%"
                        height="var(--oc-semantic-spacing-relative-75)"
                    ></oc-skeleton-v1>
                    <oc-skeleton-v1
                        variant="rectangle"
                        border-radius=""
                        width="60%"
                        height="var(--oc-semantic-spacing-relative-75)"
                    ></oc-skeleton-v1>
                </div>
            {/if}

            <!-- Color thumbs (3 circles) -->
            <div class="reptile-skeleton-tile__color-thumbs">
                {#each [1, 2, 3] as _}
                    <oc-skeleton-v1 variant="circle" width="var(--oc-semantic-spacing-relative-150)"></oc-skeleton-v1>
                {/each}
            </div>
        </div>
    </div>
{:else}
    {@render children?.()}
{/if}

<style lang="scss">
    @use '@otto-ec/design-tokens/tokens';
    @use '@otto-ec/otto-components-utils/scss/mixins' as mixins;

    .reptile-skeleton-tile {
        display: block;
        overflow: hidden;
        border-radius: tokens.$oc-semantic-border-radius-50;
        background-color: tokens.$oc-semantic-color-canvas-background;

        &--portrait &__image-wrapper {
            /*                                    */
            padding-top: 143%;
        }
        &--landscape &__image-wrapper {
            /*                                    */
            padding-top: 69.5%;
        }
        &--quad &__image-wrapper {
            /*                                    */
            padding-top: 100%;
        }

        &__image-wrapper {
            position: relative;
            display: block;
            width: 100%;
        }

        &__image {
            display: block;
            position: absolute;
            inset: 0;
        }

        &__content {
            display: flex;
            flex-direction: column;
            padding: tokens.$oc-semantic-spacing-100 tokens.$oc-semantic-spacing-relative-75
                tokens.$oc-semantic-spacing-150;

            @include mixins.breakpoint-from-l() {
                padding: tokens.$oc-semantic-spacing-100 tokens.$oc-semantic-spacing-relative-100
                    tokens.$oc-semantic-spacing-150;
            }
        }

        &__title {
            display: flex;
            flex-direction: column;
            gap: tokens.$oc-semantic-spacing-75;
            margin-bottom: tokens.$oc-semantic-spacing-150;
        }

        &__price {
            margin-bottom: tokens.$oc-semantic-spacing-150;
        }

        &__delivery {
            display: flex;
            flex-direction: column;
            gap: tokens.$oc-semantic-spacing-50;
            margin-bottom: tokens.$oc-semantic-spacing-150;
        }

        &__color-thumbs {
            display: flex;
            flex-direction: row;
            gap: tokens.$oc-semantic-spacing-50;
        }

        &--fashion {
            .reptile-skeleton-tile__content {
                padding-top: tokens.$oc-semantic-spacing-150;
            }

            .reptile-skeleton-tile__price {
                margin-bottom: tokens.$oc-semantic-spacing-100;
            }
        }
    }
</style>
